import React, { useState } from 'react';
import { Truck } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useConstruction } from '../../context/ConstructionContext';
import { toIsoDate } from '../../construction/evm';
import DataTable from '../DataTable';
import Badge from '../common/Badge';
import Button from '../common/Button';
import ConstructionShell, { FormError, Panel, inputClass, labelClass, runAction } from './ConstructionShell';
import { t } from '../../i18n';

const STATUS = {
  ok: { label: 'Conciliado', tone: 'success' },
  sobreconsumo: { label: 'Sobreconsumo', tone: 'warning' },
  subconsumo: { label: 'Bajo lo esperado', tone: 'warning' },
  sobre_recibido: { label: 'Instalado > recibido', tone: 'danger' }
};

const num = (value, decimals = 0) => Number(value || 0).toLocaleString('es-CL', { maximumFractionDigits: decimals });

const MaterialsContent = ({ summary }) => {
  const { inventory, setActivePage } = useAppContext();
  const { state, settings, can, addConsumption, userName } = useConstruction();
  const [form, setForm] = useState({ sku: '', quantity: '', wbsItemId: '', date: toIsoDate(new Date()), note: '' });
  const [error, setError] = useState('');
  const leaves = summary.evm.rows.filter((row) => row.isLeaf);
  const nameById = Object.fromEntries(summary.items.map((item) => [item.id, `${item.code} ${item.name}`]));
  const skuOptions = summary.materials.length > 0 ? summary.materials : (inventory || []).map((item) => ({ sku: item.sku, description: item.description, onSite: 0, unit: item.unit }));
  const consumptions = state.materialConsumptions.filter((entry) => entry.parkId === summary.parkId);

  const submit = (event) => {
    event.preventDefault();
    const saved = runAction(() => addConsumption({ ...form, parkId: summary.parkId, wbsItemId: form.wbsItemId || null }), setError);
    if (saved) setForm((prev) => ({ ...prev, quantity: '', note: '' }));
  };

  return (
    <div className="space-y-4">
      <Panel
        title="Conciliación despachado → instalado"
        description={t('Despachado desde Envíos a terreno, recibido en obra, instalado según registros de terreno y esperado según el avance físico. Tolerancia: ±{0}%.', settings.materialTolerancePct)}
        actions={<Button size="sm" icon={Truck} onClick={() => setActivePage('procurement-shipments')}>{t('Ir a Envíos a terreno')}</Button>}
      >
        <DataTable
          data={summary.materials.map((row) => ({ ...row, statusLabel: STATUS[row.status]?.label }))}
          columns={[
            { key: 'sku', label: 'SKU', sortable: true },
            { key: 'description', label: 'Material' },
            { key: 'unit', label: 'Unidad' },
            { key: 'dispatched', label: 'Despachado', align: 'right', render: (value) => num(value) },
            { key: 'received', label: 'Recibido en obra', align: 'right', render: (value) => num(value) },
            { key: 'installed', label: 'Instalado', align: 'right', render: (value) => num(value) },
            { key: 'onSite', label: 'En terreno', align: 'right', render: (value) => <span className={value < 0 ? 'text-danger-700 font-medium' : ''}>{num(value)}</span> },
            { key: 'planned', label: 'Planificado', align: 'right', render: (value) => num(value) },
            { key: 'expected', label: 'Esperado por avance', align: 'right', render: (value) => num(value, 1) },
            { key: 'deviationPct', label: 'Desviación', align: 'right', sortable: true, render: (value, row) => (row.planned > 0 ? `${num(value, 1)}%` : '—') },
            { key: 'statusLabel', label: 'Estado', render: (value, row) => <Badge tone={STATUS[row.status]?.tone}>{t(value)}</Badge> }
          ]}
          searchPlaceholder="Buscar material..."
          emptyTitle="Sin materiales despachados a esta obra"
          emptyDescription="Despacha materiales al proyecto desde Abastecimiento > Envíos a terreno, imputando el Área Construcción."
        />
      </Panel>

      {can('materials.edit') && (
        <Panel title="Registrar material instalado / consumido" description="Descuenta del material en terreno y alimenta la conciliación.">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div className="md:col-span-2">
              <label className={labelClass}>{t('Material')}</label>
              <select className={inputClass} value={form.sku} onChange={(event) => setForm((prev) => ({ ...prev, sku: event.target.value }))}>
                <option value="">{t('Selecciona material...')}</option>
                {skuOptions.map((row) => <option key={row.sku} value={row.sku}>{t('{0} — {1} (en terreno: {2})', row.sku, row.description, num(row.onSite))}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>{t('Cantidad')}</label>
              <input type="number" min="0" step="any" className={inputClass} value={form.quantity} onChange={(event) => setForm((prev) => ({ ...prev, quantity: event.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>{t('Partida')}</label>
              <select className={inputClass} value={form.wbsItemId} onChange={(event) => setForm((prev) => ({ ...prev, wbsItemId: event.target.value }))}>
                <option value="">{t('Sin partida asociada')}</option>
                {leaves.map((row) => <option key={row.id} value={row.id}>{`${row.code} ${row.name}`}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>{t('Fecha')}</label>
              <input type="date" className={inputClass} value={form.date} onChange={(event) => setForm((prev) => ({ ...prev, date: event.target.value }))} />
            </div>
            <div className="md:col-span-5">
              <input className={inputClass} placeholder={t('Observación (opcional)')} value={form.note} onChange={(event) => setForm((prev) => ({ ...prev, note: event.target.value }))} />
            </div>
            <Button type="submit" variant="primary" size="sm">{t('Registrar consumo')}</Button>
          </form>
          <div className="mt-2"><FormError message={error} /></div>
        </Panel>
      )}

      <Panel title="Historial de consumos" description="Material instalado en obra por partida.">
        <DataTable
          data={consumptions.map((entry) => ({ ...entry, itemName: nameById[entry.wbsItemId] || '—', author: userName(entry.userId) }))}
          columns={[
            { key: 'date', label: 'Fecha', sortable: true },
            { key: 'sku', label: 'SKU', sortable: true },
            { key: 'quantity', label: 'Cantidad', align: 'right', render: (value) => num(value, 2) },
            { key: 'itemName', label: 'Partida' },
            { key: 'author', label: 'Registrado por' },
            { key: 'note', label: 'Observación', render: (value) => value || '—' }
          ]}
          searchPlaceholder="Buscar consumo..."
          emptyTitle="Sin consumos registrados"
          emptyDescription="Registra el material instalado para conciliar contra el avance."
        />
      </Panel>
    </div>
  );
};

const ConstructionMaterials = () => (
  <ConstructionShell title="Consumo de materiales en obra" description="Cierra el ciclo Envío a terreno → instalado en obra y detecta desviaciones contra el avance.">
    {(summary) => <MaterialsContent summary={summary} />}
  </ConstructionShell>
);

export default ConstructionMaterials;
